/**
 * Central 401 handling for service API (getApiClient): one in-flight IFS re-login, then retry with fresh User-Key.
 * Coordinates with loginThunks.fetchIfsToken (shared promise) so parallel 401s queue on the same refresh.
 *
 * Does not implement OAuth steps here — dispatch(fetchIfsToken({ silent: true })) runs authService.startLogin()
 * ({ silentOnly: true }: hidden iframe + prompt=none only; no Teams auth dialog or account/login popups). After a code is obtained,
 * getIfsToken exchanges it; then this module reapplies headers and axios.request(config) once.
 *
 * Per-endpoint cap: after MAX consecutive failed refresh+retry cycles for the same method+path,
 * stops auto-retry and resets UI to login (same pattern as logoutUser state).
 */
import axios from "axios";
import store from "../store/store";
import CustomerConfig from "../../azure/CustomerConfig";
import AuthTeams from "../../auth/AuthTeams";
import { logger } from "../../utils/logger";

const DEFAULT_MAX_FAILURES = 4;
const MAX_IFS_401_FAILURES_PER_ENDPOINT = (() => {
  const raw = process.env.REACT_APP_MAX_IFS_401_FAILURES;
  const n = raw != null && raw !== "" ? parseInt(String(raw), 10) : DEFAULT_MAX_FAILURES;
  return Number.isFinite(n) && n >= 1 ? n : DEFAULT_MAX_FAILURES;
})();

/** @type {Map<string, number>} */
const endpointFailureCounts = new Map();

/**
 * @param {import('axios').InternalAxiosRequestConfig} config
 */
function normalizeRequestPath(config) {
  const u = config?.url || "";
  if (!u) return "";
  try {
    if (u.startsWith("http")) {
      return new URL(u).pathname.split("?")[0] || "";
    }
    return u.split("?")[0].replace(/^\//, "") || u;
  } catch {
    return u;
  }
}

function endpointKey(config) {
  const method = (config.method || "get").toUpperCase();
  const path = normalizeRequestPath(config);
  return `${method} ${path}`;
}

async function redirectToLoginAfterRepeated401(endpoint) {
  logger.error("IFS 401: max consecutive failures for endpoint — returning to login", {
    endpoint,
    max: MAX_IFS_401_FAILURES_PER_ENDPOINT,
  }, "API");

  endpointFailureCounts.delete(endpoint);

  const [{ ON_USER_DATA_LOAD, ON_IFS_USER_LOAD, ON_AZURE_USER_LOAD }, { USER_DATA_LOAD_STATUS, USER_TOKEN_STATUS }, { setAuthMessageBoxInfo }, { ERROR_TYPE, IFS_TOKEN_EXPIRED, IFS_401_RETRY_EXHAUSTED }] =
    await Promise.all([
      import("../constants/actionTypes"),
      import("../constants/metaData"),
      import("../slices/authFlow/authFlowSlice"),
      import("../constants/messageTypes"),
    ]);

  store.dispatch(
    setAuthMessageBoxInfo({
      type: ERROR_TYPE,
      message: IFS_TOKEN_EXPIRED,
      errorMessage: IFS_401_RETRY_EXHAUSTED,
      error: null,
    })
  );
  store.dispatch({ type: ON_IFS_USER_LOAD, payload: USER_TOKEN_STATUS.invalid });
  store.dispatch({ type: ON_AZURE_USER_LOAD, payload: USER_TOKEN_STATUS.invalid });
  store.dispatch({ type: ON_USER_DATA_LOAD, payload: USER_DATA_LOAD_STATUS.onStart });
}

function recordFailedCycle(key) {
  const next = (endpointFailureCounts.get(key) || 0) + 1;
  endpointFailureCounts.set(key, next);
  logger.warn("IFS 401 failure cycle recorded", { key, count: next, max: MAX_IFS_401_FAILURES_PER_ENDPOINT }, "API");
  return next;
}

function clearFailureCount(key) {
  endpointFailureCounts.delete(key);
}

/** For tests or manual reset */
export function resetIfs401FailureCounts() {
  endpointFailureCounts.clear();
}

/** @param {() => string} getUserKeyHeader */
export function createIfs401RetryHandler(getUserKeyHeader) {
  /**
   * Avoid refresh loops and skip routes that are not IFS-session backed or use different auth.
   * @param {import('axios').InternalAxiosRequestConfig} config
   */
  function shouldSkip(config) {
    if (!config || config.skipIfs401Retry) return true;
    if (config._ifs401Retried) return true;
    const path = normalizeRequestPath(config).toLowerCase();
    const skips = [
      "get-ifs-token",
      "get-usertoken",
      "get-customerconfig",
    ];
    return skips.some((s) => path.includes(s));
  }

  /**
   * @param {import('axios').InternalAxiosRequestConfig} config
   */
  function applyFreshServiceHeaders(config) {
    const authTeams = new AuthTeams();
    const cc = new CustomerConfig();
    const HeadersCtor = axios.AxiosHeaders;
    const h = HeadersCtor ? HeadersCtor.from(config.headers) : { ...config.headers };

    const set = (key, val) => {
      if (HeadersCtor && h instanceof HeadersCtor) {
        h.set(key, val);
      } else {
        h[key] = val;
      }
    };

    set("Ocp-Apim-Subscription-Key", cc.getSubscriptionKey());
    set("Customer-Key", authTeams.getCustomerKey() || process.env.REACT_APP_CLIENT_ID || "");
    set("User-Key", getUserKeyHeader());
    set("X-Client-Version", process.env.REACT_APP_VERSION ?? "0.0.0");

    const authz =
      (typeof h.get === "function" ? h.get("authorization") : h.authorization) ??
      (typeof h.get === "function" ? h.get("Authorization") : h.Authorization);
    const hadBearer =
      typeof authz === "string" && authz.toLowerCase().startsWith("bearer ");
    if (hadBearer) {
      const sso = localStorage.getItem("ssoUserToken");
      if (sso) {
        set("Authorization", `Bearer ${sso}`);
      }
    }

    config.headers = h;
    return config;
  }

  /**
   * @param {import('axios').AxiosError} err
   * @returns {Promise<unknown>}
   */
  return async function handleIfs401Retry(err) {
    const status = err?.response?.status;
    const config = err?.config;
    if (status !== 401 || !config) {
      return Promise.reject(err);
    }
    if (shouldSkip(config)) {
      return Promise.reject(err);
    }

    const key = endpointKey(config);
    const failuresSoFar = endpointFailureCounts.get(key) || 0;
    if (failuresSoFar >= MAX_IFS_401_FAILURES_PER_ENDPOINT) {
      await redirectToLoginAfterRepeated401(key);
      return Promise.reject(err);
    }

    config._ifs401Retried = true;
    logger.warn("API 401: attempting IFS session refresh + request retry", { url: config.url, key }, "API");

    const loginThunks = await import("../slices/login/loginThunks");
    const { fetchIfsToken, getPendingIfsTokenRefreshPromise } = loginThunks;

    try {
      const alreadyRunning = getPendingIfsTokenRefreshPromise?.();
      if (alreadyRunning) {
        await alreadyRunning;
      } else {
        await store.dispatch(fetchIfsToken({ refreshToken: true, silent: true })).unwrap();
      }
    } catch (refreshErr) {
      logger.error("IFS refresh after 401 failed", refreshErr, "API");
      recordFailedCycle(key);
      if ((endpointFailureCounts.get(key) || 0) >= MAX_IFS_401_FAILURES_PER_ENDPOINT) {
        await redirectToLoginAfterRepeated401(key);
      }
      return Promise.reject(err);
    }

    try {
      applyFreshServiceHeaders(config);
      const res = await axios.request(config);
      clearFailureCount(key);
      return res;
    } catch (retryErr) {
      recordFailedCycle(key);
      if ((endpointFailureCounts.get(key) || 0) >= MAX_IFS_401_FAILURES_PER_ENDPOINT) {
        await redirectToLoginAfterRepeated401(key);
      }
      return Promise.reject(retryErr);
    }
  };
}
